import { useMemo, useState, type Dispatch, type ReactNode, type SetStateAction } from 'react';
import {
  DndContext, KeyboardSensor, PointerSensor, closestCenter, useSensor, useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import styles from './crm.module.css';
import { CrmPopover } from './CrmPopover';
import type { CrmGridColumn, CrmSystemFieldChoice } from '../../types';
import {
  SYSTEM_FAMILY_LABELS, isSystemColumn, systemFieldGroups, type SystemFieldChoice,
} from './crmSystemFields';

type OptionsEdit = { fieldKey: string; text: string };

interface Props {
  columns: CrmGridColumn[];
  hidden: Set<string>;
  onToggle: (fieldKey: string) => void;
  /** native/direct lists show the per-column enum options editor */
  showOptionsEditor: boolean;
  fieldOptions: Record<string, string[]>;
  optionsEdit: OptionsEdit | null;
  setOptionsEdit: Dispatch<SetStateAction<OptionsEdit | null>>;
  onSaveOptions: () => void;
  savingOptions: boolean;
  /** open the add-field form (native lists only; undefined hides "+ New field") */
  onNewField?: () => void;
  /** opt-in graph-derived interaction columns this list's subject kind supports (person lists) */
  availableDerived?: { field_key: string; label: string; field_type: string }[];
  /** enable/disable the interaction-derived columns (writes config_json.derived); undefined hides the control */
  onSetDerived?: (keys: string[]) => void;
  derivedSaving?: boolean;
  /**
   * W5 (§9.3): the server-owned system-field catalog as it applies to THIS list —
   * `CrmGridResponse.available_system_fields`. Carries each field's plain-language `definition`,
   * which this panel renders in the row's ⓘ popover.
   */
  availableSystemFields?: CrmSystemFieldChoice[];
  /**
   * ⛔ There is deliberately NO admin prop here. The list-level "on for this list" control writes
   * through `onSetDerived` (`PATCH /lists/{id}/derived`, `require_crm_privileged` — owner AND
   * sharee), so eligibility is "does this caller have that writer, for one of the nine `/derived`
   * keys", never "is this caller a views admin". Gating it on the views payload's `is_admin` hid an
   * owner's own control from the owner, which §10.2 forbids in both directions.
   */
  /**
   * The GRID's live column order, as `field_key`s. The panel renders its rows in exactly this order
   * (owner request 2026-08-31) — an empty/absent array leaves `columns` in the order the caller gave.
   */
  columnOrder?: string[];
  /** Apply a new full column order to the grid. Absent ⇒ drag-to-reorder is not offered. */
  onReorder?: (orderedFieldKeys: string[]) => void;
  /** Keys the grid PINS and will not let move (the identity `name` column) — rendered undraggable. */
  pinnedKeys?: Set<string>;
}

const OPTION_TYPES = new Set(['enum', 'multi_enum', 'ranked_enum']);

// a compact DM-Mono glyph per field type so the panel reads at a glance (E4.8)
const FIELD_TYPE_GLYPH: Record<string, string> = {
  text: 'Aa', long_text: '¶', number: '#', currency: '$', date: '▤',
  enum: '◉', multi_enum: '≣', ranked_enum: '⬍',
  person: '◑', person_multi: '◑', organization: '▣', organization_multi: '▣',
};

/**
 * Put `cols` in the GRID's order (spec-free helper, unit-tested).
 *
 * A key the grid does not know keeps its incoming relative position and sorts after everything the
 * grid does know, so a column that has not yet registered with AG cannot vanish from the panel. An
 * empty `order` is the identity function — which is what keeps every pre-existing caller (and the
 * panel's own tests) rendering in the server's order.
 */
export function orderColumnsForPanel<T extends { field_key: string }>(
  cols: readonly T[], order: readonly string[] | undefined,
): T[] {
  if (!order?.length) return [...cols];
  const pos = new Map(order.map((k, i) => [k, i]));
  return cols
    .map((c, i) => ({ c, i, p: pos.get(c.field_key) ?? Number.MAX_SAFE_INTEGER }))
    .sort((a, b) => (a.p === b.p ? a.i - b.i : a.p - b.p))
    .map((x) => x.c);
}

/**
 * The full column order after dragging `activeKey` onto `overKey` (pure, unit-tested).
 *
 * Takes and returns the GRID's whole order, not the filtered rows on screen: the panel's search and
 * All/Visible tabs can hide rows between the two the user actually sees, and computing a new order
 * from the filtered list would silently reshuffle everything the filter removed.
 */
export function reorderColumnKeys(
  order: readonly string[], activeKey: string, overKey: string,
): string[] {
  const from = order.indexOf(activeKey);
  const to = order.indexOf(overKey);
  if (from < 0 || to < 0 || from === to) return [...order];
  const next = [...order];
  next.splice(from, 1);
  next.splice(to, 0, activeKey);
  return next;
}

/** One panel row: an emitted grid column, a catalog field that is off for this list, or both. */
interface PanelRow {
  key: string;
  label: string;
  fieldType: string;
  /** present when the field IS a column on the grid */
  column?: CrmGridColumn;
  /** present when the field is in the server's system-field catalog. Typed as the crmSystemFields
   *  view of a choice (the subset this panel reads) so a row can come from `systemFieldGroups`. */
  choice?: SystemFieldChoice;
  definition?: string | null;
}

interface RowProps {
  row: PanelRow;
  hidden: boolean;
  /** false for a catalog field that is OFF for this list — there is no view to show it in yet */
  canShow: boolean;
  draggable: boolean;
  onToggle: (fieldKey: string) => void;
  onInfo: (key: string, anchor: HTMLElement) => void;
  children?: ReactNode;
}

/**
 * ONE checkbox per row, everywhere: "show in this view". Everything else the row used to say in
 * prose — the field's plain-language definition, what kind of field it is, and (for an admin) the
 * list-level on/off — moved into the ⓘ popover, which is what the owner's 2026-08-31 cleanup asked
 * for. The drag handle is the ONLY drag surface, so the checkbox and the ⓘ button stay clickable.
 */
function PanelRowView({ row, hidden, canShow, draggable, onToggle, onInfo, children }: RowProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: row.key, disabled: !draggable,
  });
  return (
    <div
      ref={setNodeRef}
      className={`${styles.gridColRow} ${isDragging ? styles.colRowDragging : ''}`}
      style={{ transform: CSS.Transform.toString(transform), transition }}
    >
      {draggable ? (
        <button
          type="button"
          className={styles.colDragHandle}
          aria-label={`Reorder ${row.label}`}
          {...attributes}
          {...listeners}
        >⠿</button>
      ) : (
        <span className={styles.colDragSpacer} aria-hidden="true" />
      )}
      <label className={styles.gridColLabel}>
        <input
          type="checkbox"
          checked={canShow && !hidden}
          disabled={!canShow}
          onChange={() => canShow && onToggle(row.key)}
        />
        <span className={styles.colTypeIcon} title={row.fieldType}>
          {FIELD_TYPE_GLYPH[row.fieldType] ?? '•'}
        </span>
        {row.label}
      </label>
      <button
        type="button"
        className={styles.colInfoBtn}
        aria-label={`About ${row.label}`}
        onClick={(e) => onInfo(row.key, e.currentTarget)}
      >ⓘ</button>
      {children}
    </div>
  );
}

/**
 * "Customize View" columns panel (E4), rebuilt to the owner's 2026-08-31 cleanup.
 *
 * ## What changed, and why
 *
 * 1. **One flat, grid-ordered list.** The panel used to render four semantic sections (List specific /
 *    Global · graph-derived / View state / System fields · four families) each in the server's order,
 *    which meant the panel's order was never the grid's. The owner's requirement is that the panel
 *    read as the grid does, so the emitted columns are one list in `columnOrder` — and the "what kind
 *    of field is this?" answer the section headings carried moved into the ⓘ popover's chip, where it
 *    is per-row rather than per-section.
 * 2. **One checkbox per row = show in this view.** The SYSTEM-FIELDS rows used to carry two ticks
 *    (list-level enablement + per-view visibility) and two lines of prose. §10.2's requirement that
 *    the two operations stay DISTINCT is honoured by putting them on different surfaces rather than
 *    side by side: the row is per-view visibility, the list-level control lives in the popover.
 * 3. **The prose is gone; the copy is not.** Each row's ⓘ popover carries the server's `definition`
 *    verbatim — it is owner-calibrated text from the W5 system-fields work and is not rewritten here.
 * 4. **Drag to reorder.** `@dnd-kit` (already a dependency, already the repo's DnD library in
 *    `PlannerView`), one `SortableContext` over the emitted rows. A drag applies a new FULL column
 *    order to the grid through `onReorder`; the grid persists it exactly as a column-header drag does
 *    — into the saved view's `columnState`, saved by "Update to current view"/Save view. There is no
 *    separate column-order mutation to write to, and auto-saving on every drag would rewrite a shared
 *    view without anyone asking.
 *
 * ## What did NOT change
 *
 * Capability semantics — in EITHER direction. `onSetDerived` is still the only list-level writer this
 * panel calls, still narrowed to the nine `/derived` interaction keys, and the admin seam
 * (`PATCH /api/crm/admin/lists/{id}/system-fields`) is still one this panel does not call, so §10.2's
 * "do not widen permissions" is intact. Its converse is intact too: the control's audience is
 * unchanged from the pre-cleanup panel — whoever holds the `/derived` writer sees it. That route is
 * `require_crm_privileged` (list owner AND sharee), not `require_admin`, and §10.2 calls enable-for-list
 * "a list-owner/admin action" while amendment A3 rules that on these keys "the owner's removal wins".
 * A first cut of this cleanup gated the control on the views payload's `is_admin` — the flag that
 * gates "Set as team default" — which silently hardened a route the owner is entitled to call. What
 * the cleanup actually changed is PRESENTATION: one control instead of two side-by-side ticks, inside
 * the ⓘ popover instead of beside the per-view tick, with no italic admin notes.
 */
export function CrmColumnsPanel({
  columns, hidden, onToggle, showOptionsEditor, fieldOptions,
  optionsEdit, setOptionsEdit, onSaveOptions, savingOptions, onNewField,
  availableDerived, onSetDerived, derivedSaving, availableSystemFields,
  columnOrder, onReorder, pinnedKeys,
}: Props) {
  const [q, setQ] = useState('');
  const [tab, setTab] = useState<'all' | 'visible'>('all');
  const [info, setInfo] = useState<{ key: string; anchor: HTMLElement } | null>(null);
  const visibleCount = columns.filter((c) => !hidden.has(c.field_key)).length;

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  // the opt-in interaction-derived columns — the ONLY list-level keys this panel has a writer for.
  const interactionKeys = useMemo(
    () => new Set((availableDerived ?? []).map((a) => a.field_key)), [availableDerived]);
  // Who may flip "on for this list": whoever the caller handed a `/derived` writer to, for a key that
  // door accepts. That route is `require_crm_privileged` — the list's OWNER and its sharees — so this
  // is deliberately NOT an admin test (§10.2 + amendment A3). Same expression the pre-cleanup panel
  // used; only where the control renders changed.
  const ownerEnablable = useMemo(
    () => (onSetDerived ? interactionKeys : new Set<string>()), [onSetDerived, interactionKeys]);
  // Which system keys are ENABLED on the list, per the SERVER's effective state (`f.enabled`), never
  // inferred from the emitted columns — the §9.2 origin flip made that inference return the empty set.
  const enabledSystemKeys = useMemo(
    () => new Set((availableSystemFields ?? []).filter((f) => f.enabled).map((f) => f.field_key)),
    [availableSystemFields]);
  // The `/derived` door accepts ONLY the nine interaction keys, and only on a native list, so the
  // payload is the enabled set NARROWED to those keys before the toggle is applied. Sending a record
  // or AI-review key here would 400, and sending a narrowed-but-unrelated set would silently
  // un-enable whatever it omitted.
  const toggleDerived = (key: string) => {
    const next = new Set([...enabledSystemKeys].filter((k) => interactionKeys.has(k)));
    if (next.has(key)) next.delete(key); else next.add(key);
    onSetDerived?.([...next]);
  };

  const choiceByKey = useMemo(
    () => new Map((availableSystemFields ?? []).map((f) => [f.field_key, f])),
    [availableSystemFields]);
  const emittedKeys = useMemo(() => new Set(columns.map((c) => c.field_key)), [columns]);

  // ── the two row groups ────────────────────────────────────────────────────────────────────────
  // COLUMNS = every emitted column, in the grid's order. OFF = catalog fields this list does not
  // serve right now, which is the only thing the old per-row "Hidden for this list by an
  // administrator" note was telling anyone; the heading says it once instead of every row saying it.
  const needle = q.trim().toLowerCase();

  const columnRows: PanelRow[] = useMemo(() => {
    // search matches label OR the plain-language definition, the same rule `systemFieldGroups`
    // applies to the catalog — a user who types "internal" should find the field whose definition
    // says internal mail does not count.
    const matches = (label: string, def?: string | null) =>
      !needle || label.toLowerCase().includes(needle) || (def ?? '').toLowerCase().includes(needle);
    return orderColumnsForPanel(columns, columnOrder)
      .map((c) => {
        const choice = choiceByKey.get(c.field_key);
        return {
          key: c.field_key, label: c.label, fieldType: c.field_type, column: c, choice,
          definition: choice?.definition ?? c.definition ?? null,
        };
      })
      .filter((r) => (tab === 'visible' ? !hidden.has(r.key) : true))
      .filter((r) => matches(r.label, r.definition));
  }, [columns, columnOrder, choiceByKey, hidden, tab, needle]);

  // `systemFieldGroups` still owns eligibility (`availability === 'supported'`, `backfill_required`
  // dropped) and the search over label-or-definition, so a field the list cannot serve is still never
  // offered. Flattened here because the four families no longer head their own sections.
  const offRows: PanelRow[] = useMemo(() => {
    if (tab === 'visible') return [];
    return systemFieldGroups(availableSystemFields, q)
      .flatMap((g) => g.fields)
      .filter((f) => !emittedKeys.has(f.field_key))
      .map((f) => ({
        key: f.field_key, label: f.label, fieldType: f.field_type, choice: f,
        definition: f.definition ?? null,
      }));
  }, [availableSystemFields, q, emittedKeys, tab]);

  const infoRow = useMemo(
    () => [...columnRows, ...offRows].find((r) => r.key === info?.key),
    [columnRows, offRows, info]);

  // ── drag to reorder ───────────────────────────────────────────────────────────────────────────
  // Only EMITTED, unpinned columns are draggable: a catalog field with no column has no position in
  // the grid to move, and the identity `name` column is `lockPinned` pinned-left — the grid re-stamps
  // that pin on every applyColumnState, so a drag of it would be silently undone.
  const pinned = pinnedKeys ?? new Set<string>();
  const canDrag = !!onReorder && !!columnOrder?.length;
  const sortableIds = columnRows.filter((r) => canDrag && !pinned.has(r.key)).map((r) => r.key);
  const handleDragEnd = (e: DragEndEvent) => {
    const over = e.over;
    if (!over || !columnOrder) return;
    const activeKey = String(e.active.id);
    const overKey = String(over.id);
    if (activeKey === overKey || pinned.has(overKey)) return;
    onReorder?.(reorderColumnKeys(columnOrder, activeKey, overKey));
  };

  const optionsFor = (c: CrmGridColumn) => (
    <>
      {showOptionsEditor && OPTION_TYPES.has(c.field_type) && (
        <button
          className={styles.gridMiniBtn}
          onClick={() => setOptionsEdit(
            optionsEdit?.fieldKey === c.field_key
              ? null
              : { fieldKey: c.field_key, text: (fieldOptions[c.field_key] ?? []).join('\n') })}
        >options</button>
      )}
      {optionsEdit?.fieldKey === c.field_key && (
        <div className={styles.optionsEditor}>
          <textarea
            className={styles.optionsTextarea}
            autoFocus
            placeholder={`Options, one per line${c.field_type === 'ranked_enum' ? ' (top → bottom = rank)' : ''}`}
            value={optionsEdit.text}
            onChange={(e) => setOptionsEdit((o) => (o ? { ...o, text: e.target.value } : o))}
          />
          <div className={styles.optionsEditorActions}>
            <button className={styles.gridMiniBtn} onClick={() => setOptionsEdit(null)}>cancel</button>
            <button className={styles.gridPageBtn} disabled={savingOptions} onClick={onSaveOptions}>Save</button>
          </div>
        </div>
      )}
    </>
  );

  const openInfo = (key: string, anchor: HTMLElement) =>
    setInfo(info?.key === key ? null : { key, anchor });

  // the "what kind of field is this?" answer the section headings used to give, now per row
  const chipFor = (row: PanelRow) => {
    if (row.choice || isSystemColumn(row.column)) {
      const family = row.choice?.family ?? row.column?.system_family ?? '';
      return `System · ${SYSTEM_FAMILY_LABELS[family] ?? 'computed'}`;
    }
    if (row.column?.origin === 'derived') return 'Graph-derived';
    if (row.column?.origin === 'view') return 'View-derived';
    return 'List specific';
  };

  return (
    <>
      <input
        className={styles.setFilterSearch}
        type="search"
        placeholder="Search all fields…"
        value={q}
        onChange={(e) => setQ(e.target.value)}
      />
      <div className={styles.colTabs}>
        <button className={`${styles.colTab} ${tab === 'all' ? styles.colTabActive : ''}`} onClick={() => setTab('all')}>
          All ({columns.length})
        </button>
        <button className={`${styles.colTab} ${tab === 'visible' ? styles.colTabActive : ''}`} onClick={() => setTab('visible')}>
          Visible ({visibleCount})
        </button>
      </div>

      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={sortableIds} strategy={verticalListSortingStrategy}>
          {columnRows.length > 0 && <div className={styles.colSection}>Columns</div>}
          {columnRows.map((row) => (
            <PanelRowView
              key={row.key}
              row={row}
              hidden={hidden.has(row.key)}
              canShow
              draggable={canDrag && !pinned.has(row.key)}
              onToggle={onToggle}
              onInfo={openInfo}
            >{row.column ? optionsFor(row.column) : null}</PanelRowView>
          ))}
        </SortableContext>
      </DndContext>

      {offRows.length > 0 && <div className={styles.colSection}>Off for this list</div>}
      {offRows.map((row) => (
        <PanelRowView
          key={row.key}
          row={row}
          hidden
          canShow={false}
          draggable={false}
          onToggle={onToggle}
          onInfo={openInfo}
        />
      ))}

      {/* Three different empty states, because "no fields match" is a lie in two of them: the Visible
          tab suppresses `offRows` by design, so with every column hidden and no search term the panel
          would have blamed an empty query for a state the user created by unticking everything. */}
      {columnRows.length === 0 && offRows.length === 0 && (
        <div className={styles.gridEmpty}>
          {needle
            ? `No fields match “${q.trim()}”.`
            : tab === 'visible'
              ? 'No columns are shown in this view — tick one on the All tab.'
              : 'No fields on this list yet.'}
        </div>
      )}

      {info && infoRow && (
        <CrmPopover anchorEl={info.anchor} onClose={() => setInfo(null)} align="right">
          <div className={styles.colExplainer}>
            <div className={styles.colExplainerTitle}>{infoRow.label}</div>
            {/* ⚠ VERBATIM server copy (`available_system_fields[].definition`). It was calibrated in
                the W5 system-fields work and is not paraphrased here. */}
            {infoRow.definition && (
              <div className={styles.colExplainerDef}>{infoRow.definition}</div>
            )}
            <span className={styles.colExplainerChip}>{chipFor(infoRow)}</span>
            {/* §10.2's FIRST operation — on for this LIST, not in this view. Shown to whoever holds
                the `/derived` writer (owner or sharee — `require_crm_privileged`), never gated on the
                views-admin flag; `ownerEnablable` is the same narrowing as before, because `/derived`
                is the only list-level door this panel has ever called. */}
            {infoRow.choice && ownerEnablable.has(infoRow.key) && (
              <label className={styles.colAdminToggle}>
                <input
                  type="checkbox"
                  checked={infoRow.choice.enabled}
                  disabled={derivedSaving}
                  onChange={() => toggleDerived(infoRow.key)}
                />
                on for this list
              </label>
            )}
          </div>
        </CrmPopover>
      )}

      {onNewField && (
        <div className={styles.colPanelFooter}>
          <button className={styles.gridPageBtn} onClick={onNewField}>+ New field</button>
        </div>
      )}
    </>
  );
}
